/** Tipo do veículo = código da categoria de frota. A categoria É o tipo, e é ela
 *  que define o desenho exibido junto da placa em toda a aplicação.
 *
 *  Módulo sem React de propósito: o marcador do mapa monta a URL do vetor em DOM
 *  puro (features/mapa/markers.ts) e não deve arrastar MUI junto. */
import type { Categoria } from '../core/api/types'

/** Tipos do CAVALO: o desenho é a composição inteira, cavalo mais o que ele puxa.
 *  `cacamba` e `carvao` entraram em 08/09/2026, quando a lista do cadastro passou a
 *  ser o vocabulário da Modalidade do ERP. Ficam aqui, e não junto de FAZENDA, porque
 *  descrevem o que o cavalo PUXA: um cavalo de caçamba é cavalo. */
export const CARROCERIAS = ['espiga', 'granel', 'prancha', 'tanque', 'cacamba', 'carvao'] as const
/** Tipo da CARRETA acoplada. Sozinha ela é só o reboque, sem cavalo no desenho. */
export const CARRETA = 'trailer'
export const MAQUINA = 'maquina'
export const MAQUINAS = [MAQUINA] as const
/** A plataforma que acompanha a máquina (04/09/2026): o único tipo de veículo
 *  que continua cadastrado à mão, e entra no conjunto de máquinas ao lado do
 *  operador e da máquina. */
export const PLATAFORMA = 'plataforma'
/** Veículo administrativo, como a caminhonete de apoio. Entra no cadastro porque o
 *  Protheus manda a frota inteira, e fica FORA de CARROCERIAS de propósito: assim
 *  não aparece no Quadro de Frota nem nos seletores da operação.
 *
 *  Chama-se "Frota Leve" na tela desde 08/09/2026, que é o nome do ERP para o mesmo
 *  veículo. O CODE segue `utilitario`, e com ele o vetor e os seletores. */
export const UTILITARIO = 'utilitario'
/** Modalidade do ERP que não descreve carroceria. */
export const FAZENDA = 'fazenda'
/** O que a origem tem e o HUB não tipa: despendoadeira, plantadeira, pulverizador,
 *  empilhadeira e caminhão rígido. Também fora de CARROCERIAS. */
export const OUTROS = 'outros'
export const FLEET_TIPOS = [
  ...CARROCERIAS, CARRETA, ...MAQUINAS, PLATAFORMA, UTILITARIO, FAZENDA, OUTROS,
] as const

/** Filtro `?category=` do seletor de cavalo. A carreta usa `CARRETA` direto. */
export const CARROCERIAS_PARAM = CARROCERIAS.join(',')

/** Frota sem tipo conhecido, ou rastreador que não casou com nenhum cadastro,
 *  cai no caminhão genérico em vez de sumir da tela. */
const FALLBACK = 'truck'

/** Grupos do filtro de categoria do Mapa, na linguagem da operação: Máquina reúne
 *  trator e colheitadeira, e Combustível é o tipo Tanque. Carreta e frota sem tipo
 *  ficam de fora do filtro — aparecem apenas quando nenhum grupo está escolhido. */
export const FLEET_GRUPOS = [
  { value: 'espiga', label: 'Espiga', codes: ['espiga'] },
  { value: 'granel', label: 'Granel', codes: ['granel'] },
  { value: 'prancha', label: 'Prancha', codes: ['prancha'] },
  { value: 'maquina', label: 'Máquina', codes: [...MAQUINAS] },
  { value: 'combustivel', label: 'Combustível', codes: ['tanque'] },
] as const

export type FleetGrupo = (typeof FLEET_GRUPOS)[number]['value']

/** Códigos de tipo que compõem um grupo. Grupo desconhecido não filtra nada. */
export function fleetGrupoCodes(grupo: string): readonly string[] {
  return FLEET_GRUPOS.find((g) => g.value === grupo)?.codes ?? []
}

/** Proporção original dos vetores (viewBox 1400×781 ≈ 1.79:1), preservada nos dois
 *  tamanhos para o veículo não distorcer. */
export const FLEET_ICON_SIZES = {
  grid: { width: 44, height: 25 },
  inline: { width: 26, height: 15 },
} as const

export type FleetIconSize = keyof typeof FLEET_ICON_SIZES

/** Tipo que ainda não tem vetor próprio empresta o de outro, em vez de pedir um
 *  arquivo inexistente e quebrar a imagem. Sai daqui quando o desenho existir:
 *  utilitário e plataforma saíram em 04/09/2026, e máquina em 15/09/2026, quando
 *  a colheitadeira de `vetores/svg/colheitadeira.svg` passou a responder por
 *  `maquina.svg`. Ela é o caso dominante do rodado "outros" do ERP; plantadeira e
 *  pulverizador seguem sob o mesmo desenho até ganharem tipo próprio. */
const ICONE_EMPRESTADO: Record<string, string> = {
  [OUTROS]: FALLBACK,
  // Fazenda não descreve uma silhueta específica no catálogo.
  [FAZENDA]: FALLBACK,
}

/** WebP desde 05/10/2026, no quádruplo do marcador do Mapa, o maior uso. Os SVG
 *  de 1400 por 781, com centenas de traços cada, somavam 1,5 MB para desenhar no
 *  máximo 51 por 29 pixels, e o navegador rasterizava cada um por marcador. Os SVG
 *  seguem ao lado, como fonte para gerar de novo. */
export function fleetIconUrl(code: string | null | undefined): string {
  const tipo = code && (FLEET_TIPOS as readonly string[]).includes(code) ? code : FALLBACK
  return `/assets/frota/${ICONE_EMPRESTADO[tipo] ?? tipo}.webp`
}

/** Rótulo do tipo para `alt`/`title`. Sem categoria não inventa nome. */
export function fleetIconLabel(category: Categoria | null | undefined): string {
  return category?.label ?? 'Sem tipo'
}
